import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Hotel, MapPin, ShieldCheck, Globe, Phone, MessageCircle, BadgePercent, BedDouble, Plane, CalendarCheck2 } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { LeadForm } from "../components/hotelspulse/LeadForm";
import { HPHeader } from "../components/hotelspulse/HPHeader";
import { HPFooter } from "../components/hotelspulse/HPFooter";
import { ConciergeChat } from "../components/hotelspulse/ConciergeChat";

const BADGE_LABEL = { identity_verified: "Identity Verified", location_verified: "Location Verified", contact_verified: "Contact Verified", hotel_managed: "Hotel-Managed Page", rates_updated: "Rates Recently Updated", photos_verified: "Photos Verified", direct_booking: "Direct Booking Available", responsive_hotel: "Responsive Hotel", tourism_registration_verified: "Tourism Registration Verified" };

export default function HotelsPulseProperty() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/directstay/properties/${slug}`).then((r) => setP(r.data.property)).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <div className="font-heading font-900 text-2xl">Property not found</div>
      <Link to="/hotelspulse/stays" className="text-emerald-700 underline mt-2 inline-block">Browse verified stays</Link>
    </div>
  );
  if (!p) return <Spinner full />;

  const wa = p.contact?.whatsapp?.replace(/\D/g, "");

  return (
    <div data-testid="hotelspulse-property">
      <HPHeader />
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">{(p.property_type || "").replace(/_/g, " ")}</div>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight">{p.name}</h1>
          <div className="text-zinc-300 mt-2 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[p.location?.address, p.location?.city, p.location?.state, p.location?.country].filter(Boolean).join(", ")}</div>
          <div className="flex flex-wrap gap-2 mt-4">
            {(p.badges || []).map((b) => (
              <span key={b} data-testid={`badge-${b}`} className="inline-flex items-center gap-1.5 bg-emerald-900 border border-emerald-700 text-emerald-200 text-xs px-3 py-1.5"><ShieldCheck className="w-3.5 h-3.5 text-amber-400" /> {BADGE_LABEL[b] || b}</span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {p.photos?.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {p.photos.slice(0, 4).map((url, i) => <img key={i} src={url} alt={`${p.name} ${i + 1}`} className={`w-full object-cover ${i === 0 ? "col-span-2 h-72" : "h-40"}`} />)}
            </div>
          )}

          {p.description && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-2">About this property</h2>
              <p className="text-zinc-700 whitespace-pre-line">{p.description}</p>
              {p.distinctive && <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 p-3 mt-3"><strong>What makes it special:</strong> {p.distinctive}</p>}
            </section>
          )}

          {p.offers?.length > 0 && (
            <section data-testid="offers-section">
              <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2"><BadgePercent className="w-5 h-5 text-amber-500" /> Direct-booking offers</h2>
              <div className="space-y-2">
                {p.offers.map((o) => (
                  <div key={o.id} className="border border-amber-300 bg-amber-50 p-4">
                    <div className="font-700">{o.title}</div>
                    {o.description && <p className="text-sm text-zinc-600 mt-0.5">{o.description}</p>}
                    {o.valid_till && <p className="text-xs text-amber-700 mt-1">Valid until {o.valid_till}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {p.rooms?.length > 0 && (
            <section data-testid="rooms-section">
              <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2"><BedDouble className="w-5 h-5 text-emerald-700" /> Rooms</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.rooms.map((r, i) => (
                  <div key={i} className="border border-zinc-300 bg-white p-4">
                    <div className="font-heading font-900">{r.name}</div>
                    <div className="text-sm text-zinc-500">Sleeps {r.occupancy}</div>
                    {r.base_rate && <div className="text-sm mt-1"><span className="font-700 text-emerald-700">{r.base_rate}</span> <span className="text-xs text-zinc-400">/ night · typical direct rate — confirm with property</span></div>}
                    {Array.isArray(r.amenities) && r.amenities.length > 0 && <div className="text-xs text-zinc-500 mt-1">{r.amenities.join(" · ")}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {p.features?.length > 0 && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-3">Features & amenities</h2>
              <div className="flex flex-wrap gap-2">{p.features.map((ft) => <span key={ft} className="border border-zinc-300 px-3 py-1.5 text-sm">{ft}</span>)}</div>
            </section>
          )}

          {(p.location_intel?.nearest_airport || p.location_intel?.best_season) && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-3">Getting there</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.location_intel?.nearest_airport && <div className="border border-zinc-300 p-4 flex items-start gap-3"><Plane className="w-5 h-5 text-emerald-700 mt-0.5" /><div><div className="text-xs uppercase text-zinc-400 font-600">Nearest airport</div><div className="text-sm">{p.location_intel.nearest_airport}</div></div></div>}
                {p.location_intel?.best_season && <div className="border border-zinc-300 p-4 flex items-start gap-3"><CalendarCheck2 className="w-5 h-5 text-emerald-700 mt-0.5" /><div><div className="text-xs uppercase text-zinc-400 font-600">Best season</div><div className="text-sm">{p.location_intel.best_season}</div></div></div>}
              </div>
            </section>
          )}

          {p.faq?.length > 0 && (
            <section data-testid="faq-section">
              <h2 className="font-heading font-900 text-xl mb-3">Frequently asked questions</h2>
              <div className="space-y-2">
                {p.faq.map((f, i) => (
                  <details key={i} className="border border-zinc-200 bg-white p-4">
                    <summary className="font-700 text-sm cursor-pointer">{f.q}</summary>
                    <p className="text-sm text-zinc-600 mt-2 whitespace-pre-line">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <p className="text-xs text-zinc-400 border-t border-zinc-200 pt-4">
            {p.claimed ? "This page is managed by the property." : "This page has not been claimed by the property yet."} Last updated {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}.
            Rates shown are typical direct rates supplied by the property — always confirm availability and final pricing directly.
          </p>
        </div>

        {/* Sidebar: contact + lead form */}
        <div className="space-y-4">
          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-900">Contact directly</div>
            {p.contact?.website && <a data-testid="contact-website" href={p.contact.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-700 hover:underline"><Globe className="w-4 h-4" /> Official website</a>}
            {p.contact?.booking_url && <a data-testid="contact-booking" href={p.contact.booking_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-700 hover:underline"><Hotel className="w-4 h-4" /> Book direct on hotel site</a>}
            {p.contact?.phone && <a href={`tel:${p.contact.phone}`} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline"><Phone className="w-4 h-4" /> {p.contact.phone}</a>}
            {wa && <a data-testid="contact-whatsapp" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-700 hover:underline"><MessageCircle className="w-4 h-4" /> WhatsApp the property</a>}
          </div>
          <LeadForm property={p} />
        </div>
      </div>
      <ConciergeChat property={p} />
      <HPFooter />
    </div>
  );
}
